import { PdAddress } from 'interfaces/address';
import { EffectiveCart } from 'interfaces/cart';
import type { ExpressCheckoutSummary } from 'interfaces/checkout';
import type { PdProductItem, PdSubscriptionProductItem } from 'interfaces/line-item';
import { isSubscriptionItem } from 'interfaces/line-item';
import type { ProductType } from 'interfaces/product';
import type { Checkout } from 'tracking/events/commons';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface TrackedListItem {
  productId: string;
  list: string;
}

export interface GtmCheckoutItem {
  interval?: string;
  list?: string;
  price: number;
  productId: string;
  productTitle: string;
  productType: ProductType;
  quantity: number;
  sku: string;
  tags: string;
  taxRate: number;
  variantId: string;
  variantTitle: string;
}

export interface Summary {
  requestId: string;
  effectiveCart: EffectiveCart;
  addresses: {
    shipping?: PdAddress;
    billing?: PdAddress;
  };
  payment?: {
    gateway: string;
    method: string;
  };
  customer: {
    email: string;
    id: number;
    isGuest: boolean;
    ordersCount: number;
    segments: string[];
  };
}

interface TrackingStore {
  trackedList: TrackedListItem[];
  trackedPurchases: string[];
  isMandate: boolean;
  addTrackedPurchase: (transactionId: string) => void;
  clearTrackedList: () => void;
  getProductList: (productId: string, defaultListName: string) => string;
  hasTrackedPurchase: (transactionId: string) => boolean;
  mapCheckoutItems: (items: Array<PdProductItem | PdSubscriptionProductItem>) => GtmCheckoutItem[];
  mapPurchase: (summary: Summary | ExpressCheckoutSummary) => Checkout;
  setTrackedList: (productId: string, list: string) => void;
  setIsMandate: (isMandate: boolean) => void;
}

/**
 * Zustand Store to manage product list tracking for performance analysis
 */
export const useTrackingStore = create(
  persist<TrackingStore>(
    (set, get) => ({
      trackedList: [],
      isMandate: false,
      setIsMandate: (isMandate) => set({ isMandate }),
      setTrackedList: (productId, list) => {
        const exists = get().trackedList.some((item) => item.productId === productId);
        if (exists) return;
        set((state) => ({
          trackedList: [...state.trackedList, { productId, list }],
        }));
      },
      getProductList: (productId, defaultListName) => {
        const found = get().trackedList.find((item) => item.productId + '' === productId + '');
        if (!found) {
          get().setTrackedList(productId + '', defaultListName);
          return defaultListName;
        }
        return found.list;
      },
      clearTrackedList: () => set({ trackedList: [] }),
      mapCheckoutItems: (items) => {
        return items.map((item) => {
          const interval = isSubscriptionItem(item) ? item.interval : undefined;
          const list = get().getProductList(item.productId + '', '');

          return {
            interval,
            list,
            price: item.price,
            productId: item.productId + '',
            productTitle: item.productTitle,
            productType: item.productType,
            quantity: item.quantity,
            sku: item.sku,
            tags: item.tags,
            taxRate: item.taxRatios
              .map(([rate, ratio]) => rate * ratio)
              .reduce((sum, rate) => sum + rate, 0),
            variantId: item.variantId + '',
            variantTitle: item.variantTitle + '',
          };
        });
      },
      mapPurchase: (summary) => {
        const sumTaxBrackets = (
          sum: number,
          bracket: [number, number] | { amount: number }
        ): number => sum + ('amount' in bracket ? bracket.amount : bracket[1]);

        const {
          customer,
          payment,
          requestId,
          effectiveCart: { cartTotal, discount, items, itemsTotal, shipping, taxes },
        } = summary;

        const itemsVat = taxes.items.reduce(sumTaxBrackets, 0);
        const shippingVat = taxes.shipping?.reduce(sumTaxBrackets, 0) || 0;
        const discountVat = Math.abs(taxes.discount?.reduce(sumTaxBrackets, 0) || 0);
        const paymentData = payment
          ? {
              gateway: payment.gateway,
              method: payment.method,
            }
          : undefined;

        return {
          requestId,
          customer: {
            id: customer.id,
            email: customer.email,
            ordersCount: customer.ordersCount,
          },
          totalAmount: cartTotal,
          totalVat: itemsVat + shippingVat - discountVat,
          itemsTotal,
          payment: paymentData,
          shipping: {
            type: shipping.type,
            price: shipping.price,
            vat: shippingVat,
          },
          discount: discount
            ? {
                type: discount.type,
                code: discount.code,
                value: discount.fixedValue,
              }
            : undefined,
          items: get().mapCheckoutItems(items),
        };
      },
      trackedPurchases: [],
      hasTrackedPurchase: (transactionId) => {
        return get().trackedPurchases.includes(transactionId);
      },
      addTrackedPurchase: (transactionId) => {
        if (!get().trackedPurchases.includes(transactionId)) {
          set((state) => ({
            trackedPurchases: [...state.trackedPurchases, transactionId],
          }));
        }
      },
    }),
    {
      name: 'pd:track-list-performance',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
